import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { fullCmiGuard } from './core/tenant/full-cmi.guard';
import { callbackGuard } from './core/auth/callback.guard';
import { agentsNavEnabledGuard } from './core/feature-flags/agents-nav-enabled.guard';
import { customAgentGuard } from './core/feature-flags/custom-agent.guard';
import { columnMappingAdminGuard } from './core/auth/column-mapping-admin.guard';
import { dataScopeAdminGuard } from './core/auth/data-scope-admin.guard';
import { diveEmbedAdminGuard } from './core/auth/dive-embed-admin.guard';
import { pricingActionAdminGuard } from './core/auth/pricing-action-admin.guard';
import { userPermissionsAdminGuard } from './core/auth/user-permissions-admin.guard';
import { userTypesAdminGuard } from './core/auth/user-types-admin.guard';
import { tenantMembershipsAdminGuard } from './core/auth/tenant-memberships-admin.guard';
import { emailSubscriptionsAdminGuard } from './core/auth/email-subscriptions-admin.guard';
import { priceBandRunAdminGuard } from './core/auth/price-band-run-admin.guard';
import { uiConfigAdminGuard } from './core/auth/ui-config-admin.guard';
import { adminOverviewGuard } from './core/auth/admin-overview.guard';
import { insightReviewGuard } from './core/auth/insight-review.guard';
import { insightStudioGuard } from './core/auth/insight-studio.guard';
import { pricingActionPermissionGuard } from './core/auth/pricing-action-permission.guard';
import {
  PRICING_ACTION_VIEW_OWN,
  PRICING_ACTION_MARGIN_REVIEW,
  PRICING_ACTION_VIEW_ORG,
  PRICING_ACTION_VIEW_TEAM,
} from './core/auth/pricing-action-permissions';
import { configurationNavEnabledGuard } from './core/feature-flags/configuration-nav-enabled.guard';
import { pricingEnabledGuard } from './core/feature-flags/pricing-enabled.guard';
import { pricingActionLoopEnabledGuard } from './core/feature-flags/pricing-action-loop-enabled.guard';
import { reviewBandsEnabledGuard } from './core/feature-flags/review-bands-enabled.guard';
import { bandAdherenceEnabledGuard } from './core/feature-flags/band-adherence-enabled.guard';
import { performancePageEnabledGuard } from './core/feature-flags/performance-page-enabled.guard';
import { scorecardEnabledGuard } from './core/feature-flags/scorecard-enabled.guard';
import { capabilityGuard } from './core/auth/capability.guard';
import { bandStudioGuard } from './core/auth/band-studio.guard';
import { modelVersionRedirect } from './pages/pricing/band-studio/model-version-redirect';
import { accessRolesAdminGuard } from './core/auth/tenant-admin-tool.guard';
import {
  BAND_STUDIO_MANAGE,
  BAND_STUDIO_VIEW,
  CHAT_USE,
  CUSTOM_REPORTS_VIEW,
  DASHBOARDS_VIEW,
  INSIGHTS_VIEW,
  PRICING_VIEW,
  REVIEW_BANDS_VIEW,
  SCORECARDS_VIEW,
  TABLE_EXPORT_CAPABILITY,
} from './core/auth/capabilities';
import {
  CmiDrilldownConfig,
  cmiViewAllConfig,
  deriveCmiAggregateViews,
} from './shared/models/cmi-insight-routes.config';

/**
 * `reports` and `reports/:reportId` as ONE route config, so opening or closing a
 * report keeps the page instance (and its loaded list) instead of recreating it.
 */
export function studioReportsMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments[0]?.path !== 'reports' || segments.length > 2) return null;
  return { consumed: segments, posParams: segments[1] ? { reportId: segments[1] } : {} };
}

export const routes: Routes = [
  {
    path: 'login/complete',
    canActivate: [callbackGuard],
    loadComponent: () => import('./core/auth/callback/login.complete').then((m) => m.LoginComplete),
  },
  {
    path: 'login',
    loadComponent: () => import('./core/auth/pages/login/login').then((m) => m.Login),
  },
  {
    // Public: no guard and no app shell, so it loads even when sign-in is broken.
    path: 'status',
    loadComponent: () => import('./pages/status/status').then((m) => m.StatusPage),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/app-layout').then((m) => m.AppLayout),
    // fullCmiGuard is first on every child a pricing-action-only tenant must not reach;
    // app.routes.full-cmi.spec.ts refuses an unclassified child.
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        canActivate: [fullCmiGuard],
        // A parent guard does not re-run on a navigation into a child, and Home is the one
        // gated route such a tenant can stand on (the denial terminal state).
        canActivateChild: [fullCmiGuard, capabilityGuard(INSIGHTS_VIEW)],
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./pages/home/home').then((m) => m.Home),
          },
          {
            path: 'insights',
            loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
            data: {
              cmiViewAllConfig: cmiViewAllConfig(''),
            },
          },
        ],
      },
      {
        // Custom Reports: a tenant's published MotherDuck Dives, as a browsable grid
        // rather than fixed per-page placements.
        path: 'custom-reports',
        canActivate: [fullCmiGuard],
        canActivateChild: [capabilityGuard(CUSTOM_REPORTS_VIEW)],
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/custom-reports/custom-reports').then((m) => m.CustomReports),
          },
          {
            path: ':placement',
            loadComponent: () =>
              import('./pages/custom-reports/custom-report-detail').then(
                (m) => m.CustomReportDetail
              ),
          },
        ],
      },
      {
        // A parent's guards run for every descendant, so each dashboard's key gates its index alone.
        path: 'sales-rep-performance',
        canActivate: [fullCmiGuard],
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [capabilityGuard(DASHBOARDS_VIEW)],
            loadComponent: () =>
              import('./pages/sales-rep-performance/sales-rep-performance').then(
                (m) => m.SalesRepPerformance
              ),
          },
          {
            path: 'coaching-revenue',
            canActivateChild: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: cmiViewAllConfig('coaching_revenue'),
                },
              },
              {
                path: ':repName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'coaching_revenue',
                    entityParamKey: 'repName',
                    backRoute: '/sales-rep-performance',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'coaching-pricing',
            canActivateChild: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: cmiViewAllConfig('coaching_pricing'),
                },
              },
              {
                path: ':repName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'coaching_pricing',
                    entityParamKey: 'repName',
                    backRoute: '/sales-rep-performance',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'summary/:repName',
            canActivate: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./pages/insight-drilldown/insight-drilldown').then(
                (m) => m.InsightDrilldownPage
              ),
            data: {
              cmiDrilldownConfig: {
                investigationType: 'sales_rep_summary',
                entityParamKey: 'repName',
                backRoute: '/sales-rep-performance',
              } satisfies CmiDrilldownConfig,
            },
          },
        ],
      },
      {
        path: 'customer-performance',
        canActivate: [fullCmiGuard],
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [capabilityGuard(DASHBOARDS_VIEW)],
            loadComponent: () =>
              import('./pages/customer-performance/customer-performance').then(
                (m) => m.CustomerPerformance
              ),
          },
          {
            path: 'win-back-opportunities',
            canActivateChild: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: cmiViewAllConfig('customer_win_back'),
                },
              },
              {
                path: ':customerName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'customer_win_back',
                    entityParamKey: 'customerName',
                    backRoute: '/customer-performance',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'customers-at-risk',
            canActivateChild: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: cmiViewAllConfig('customer_at_risk'),
                },
              },
              {
                path: ':customerName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'customer_at_risk',
                    entityParamKey: 'customerName',
                    backRoute: '/customer-performance',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'cross-sell-opportunities',
            canActivateChild: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: cmiViewAllConfig('market_basket_cross_sell'),
                },
              },
              {
                path: ':customerName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'market_basket_cross_sell',
                    entityParamKey: 'customerName',
                    backRoute: '/customer-performance',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'summary/:customerName',
            canActivate: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./pages/insight-drilldown/insight-drilldown').then(
                (m) => m.InsightDrilldownPage
              ),
            data: {
              cmiDrilldownConfig: {
                investigationType: 'customer_summary',
                entityParamKey: 'customerName',
                backRoute: '/customer-performance',
              } satisfies CmiDrilldownConfig,
            },
          },
        ],
      },
      {
        path: 'product-performance',
        canActivate: [fullCmiGuard],
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [capabilityGuard(DASHBOARDS_VIEW)],
            loadComponent: () =>
              import('./pages/product-performance/product-performance').then(
                (m) => m.ProductPerformance
              ),
          },
          {
            path: 'price-not-keeping-up-with-cost',
            canActivateChild: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: cmiViewAllConfig('price_vs_cost'),
                },
              },
              {
                path: ':product',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'price_vs_cost',
                    entityParamKey: 'product',
                    backRoute: '/product-performance',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'products-in-decline',
            canActivateChild: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: cmiViewAllConfig('product_decline'),
                },
              },
              {
                path: ':product',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'product_decline',
                    entityParamKey: 'product',
                    backRoute: '/product-performance',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'summary/:product',
            canActivate: [capabilityGuard(INSIGHTS_VIEW)],
            loadComponent: () =>
              import('./pages/insight-drilldown/insight-drilldown').then(
                (m) => m.InsightDrilldownPage
              ),
            data: {
              cmiDrilldownConfig: {
                investigationType: 'product_summary',
                entityParamKey: 'product',
                backRoute: '/product-performance',
              } satisfies CmiDrilldownConfig,
            },
          },
        ],
      },
      {
        path: 'agents/customer-risk',
        canActivate: [fullCmiGuard, agentsNavEnabledGuard, capabilityGuard(CHAT_USE)],
        loadComponent: () => import('./shared/components/chat/chat').then((m) => m.ChatComponent),
        data: { chatConfigKey: 'customer-risk' },
      },
      {
        path: 'agents/product-pricing',
        canActivate: [fullCmiGuard, agentsNavEnabledGuard, capabilityGuard(CHAT_USE)],
        loadComponent: () => import('./shared/components/chat/chat').then((m) => m.ChatComponent),
        data: { chatConfigKey: 'product-pricing' },
      },
      {
        path: 'agents/sales-rep-performance',
        canActivate: [fullCmiGuard, agentsNavEnabledGuard, capabilityGuard(CHAT_USE)],
        loadComponent: () => import('./shared/components/chat/chat').then((m) => m.ChatComponent),
        data: { chatConfigKey: 'sales-rep' },
      },
      {
        path: 'agents/custom/:slug',
        canActivate: [fullCmiGuard, customAgentGuard, capabilityGuard(CHAT_USE)],
        loadComponent: () =>
          import('./pages/agents/custom-agent-page/custom-agent-page').then(
            (m) => m.CustomAgentPage
          ),
      },
      {
        path: 'price-not-keeping-up-with-cost',
        redirectTo: 'product-performance/price-not-keeping-up-with-cost',
        pathMatch: 'full',
      },
      {
        path: 'customers-at-risk',
        redirectTo: 'customer-performance/customers-at-risk',
        pathMatch: 'full',
      },
      {
        // Componentless parent: the sidebar is the only way between these pages.
        // It is gated only by tenant entitlement, so each surface underneath
        // answers to its own flag alone — `pricing-enabled` belongs to the
        // Pricing dashboard, not to the band surfaces beside it. Guards on the
        // parent would apply to every child and make one flag gate them all.
        path: 'pricing',
        canActivate: [fullCmiGuard],
        children: [
          {
            // The Pricing dashboard — the surface `pricing-enabled` actually
            // gates, matching the Pricing entry under Margin Levers.
            path: '',
            pathMatch: 'full',
            canActivate: [pricingEnabledGuard, capabilityGuard(PRICING_VIEW)],
            loadComponent: () => import('./pages/pricing/pricing').then((m) => m.Pricing),
          },
          {
            // Review Bands — review/filter/give-feedback on the latest Price+
            // band run. Gated by its own `review-bands-enabled` Amplitude flag
            // (off in deployed envs until rolled out) and nothing else.
            path: 'review-bands',
            canActivate: [reviewBandsEnabledGuard, capabilityGuard(REVIEW_BANDS_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/review-bands/review-bands').then((m) => m.ReviewBands),
          },
          // Bands Studio (Pricing → Models): band models, their versions and runs.
          // Needs `band_studio.view` (a role opt-in; staff hold it) while the
          // `band-studio-enabled` flag is on, `models/new` `band_studio.manage`; the static paths precede
          // `models/:slug` so "new" and "compare" are never read as a slug.
          {
            path: 'models',
            canActivate: [bandStudioGuard, capabilityGuard(BAND_STUDIO_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/band-studio/models-page/models-page').then(
                (m) => m.ModelsPage
              ),
          },
          {
            path: 'models/new',
            canActivate: [bandStudioGuard, capabilityGuard(BAND_STUDIO_MANAGE)],
            loadComponent: () =>
              import('./pages/pricing/band-studio/new-model/new-model').then((m) => m.NewModelPage),
          },
          {
            path: 'models/compare',
            canActivate: [bandStudioGuard, capabilityGuard(BAND_STUDIO_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/band-studio/compare/compare-page').then((m) => m.ComparePage),
          },
          {
            path: 'models/:slug',
            canActivate: [bandStudioGuard, capabilityGuard(BAND_STUDIO_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/band-studio/model-summary/model-summary').then(
                (m) => m.ModelSummaryPage
              ),
          },
          {
            path: 'models/:slug/draft',
            canActivate: [bandStudioGuard, capabilityGuard(BAND_STUDIO_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/band-studio/model-summary/model-summary').then(
                (m) => m.ModelSummaryPage
              ),
          },
          {
            // the old address of a model's saved logic; a model is its run, so it opens there.
            // No guards: the router runs none on a redirect, so the redirect checks the user
            // itself before it reads anything, and the page it lands on carries the Studio's.
            path: 'models/:slug/v/:version',
            redirectTo: modelVersionRedirect,
          },
          {
            // one model run, read from the logic it ran (a bundle file has no version page)
            path: 'models/:slug/run/:runId',
            canActivate: [bandStudioGuard, capabilityGuard(BAND_STUDIO_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/band-studio/model-summary/model-summary').then(
                (m) => m.ModelSummaryPage
              ),
          },
          {
            // GF-4603 Impact — guidance-impact pivot (price vs target impact by
            // guidance type × go-live month). Gated by the `band_adherence`
            // Amplitude flag alongside the Adherence tab.
            path: 'impact',
            canActivate: [bandAdherenceEnabledGuard, capabilityGuard(REVIEW_BANDS_VIEW)],
            loadComponent: () => import('./pages/pricing/impact/impact').then((m) => m.Impact),
          },
          {
            // Performance — guidance impact over launches and periods; will replace Impact.
            path: 'performance',
            canActivate: [performancePageEnabledGuard, capabilityGuard(REVIEW_BANDS_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/performance/performance').then((m) => m.Performance),
          },
          {
            // GF-4603 Adherence — pricing-guidance adherence (pre→post
            // transition): KPI cards, quick summary, band-position stacked bar,
            // and an expandable per-rep grid. Gated by the `band_adherence`
            // Amplitude flag.
            path: 'adherence',
            canActivate: [bandAdherenceEnabledGuard, capabilityGuard(REVIEW_BANDS_VIEW)],
            loadComponent: () =>
              import('./pages/pricing/adherence/adherence').then((m) => m.Adherence),
          },
        ],
      },
      {
        path: 'scorecard',
        canActivate: [fullCmiGuard, scorecardEnabledGuard, capabilityGuard(SCORECARDS_VIEW)],
        loadComponent: () => import('./pages/scorecard/scorecard').then((m) => m.Scorecard),
      },
      {
        // Pricing Action Loop shell. The `pricing-action-loop` flag and
        // the active tenant's entitlement both gate the whole subtree, and the
        // shell also evicts a mounted page when that entitlement drops. Each role
        // surface is a lazy child behind its own DB-permission guard (bounces
        // unauthorized deep-links to /home with no flicker). `authGuard` is
        // inherited from the AppLayout parent and no resolvers are declared —
        // mirroring the /pricing + /scorecard pattern.
        path: 'pricing-action',
        canActivate: [pricingActionLoopEnabledGuard],
        providers: [{ provide: TABLE_EXPORT_CAPABILITY, useValue: null }],
        loadComponent: () =>
          import('./pages/pricing-action/pricing-action-shell').then((m) => m.PricingActionShell),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'queue' },
          {
            // My Queue: the margin queue narrowed to the rows assigned to the
            // reader — the same page as /margin under a route preset. Its own
            // key is held by every membership role. The ROWS are the reader's
            // own; the response also carries week-level run metadata and totals,
            // which the banner's notices are built from, so this is not an
            // exclusively reader-scoped payload.
            path: 'queue',
            canActivate: [pricingActionPermissionGuard(PRICING_ACTION_VIEW_OWN)],
            data: { marginQueueScope: 'mine' },
            loadComponent: () =>
              import('./pages/pricing-action/margin/margin-queue').then((m) => m.MarginQueuePage),
          },
          {
            path: 'manager',
            canActivate: [pricingActionPermissionGuard(PRICING_ACTION_VIEW_TEAM)],
            loadComponent: () =>
              import('./pages/pricing-action/pricing-action-manager').then(
                (m) => m.PricingActionManager
              ),
          },
          {
            path: 'exec',
            canActivate: [pricingActionPermissionGuard(PRICING_ACTION_VIEW_ORG)],
            loadComponent: () =>
              import('./pages/pricing-action/pricing-action-exec').then((m) => m.PricingActionExec),
          },
          {
            // The margin-compression queue. Its own DB permission, granted to
            // sales ops rather than to sellers: nothing here notifies a rep or
            // suggests a price, and the loop's commit/escalate rights say
            // nothing about who may read a week's margin moves.
            path: 'margin',
            canActivate: [pricingActionPermissionGuard(PRICING_ACTION_MARGIN_REVIEW)],
            loadComponent: () =>
              import('./pages/pricing-action/margin/margin-queue').then((m) => m.MarginQueuePage),
          },
        ],
      },
      {
        path: 'cmi/insights/:insightId',
        canActivate: [fullCmiGuard, capabilityGuard(INSIGHTS_VIEW)],
        loadComponent: () =>
          import('./pages/insight-drilldown/insight-drilldown').then((m) => m.InsightDrilldownPage),
      },
      {
        // Insight Studio: privileged-only workspace. The shell owns the page
        // scaffold and tab strip; each section is a lazy child in its outlet.
        path: 'studio',
        canActivate: [fullCmiGuard, insightStudioGuard],
        loadComponent: () =>
          import('./pages/studio/studio-shell/studio-shell').then((m) => m.StudioShell),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'agents' },
          {
            path: 'agents',
            loadComponent: () =>
              import('./pages/studio/agents/studio-agents').then((m) => m.StudioAgentsPage),
          },
          {
            path: 'agents/:slug',
            loadComponent: () =>
              import('./pages/studio/agents/studio-agents').then((m) => m.StudioAgentsPage),
          },
          {
            path: 'insight-agents',
            loadComponent: () =>
              import('./pages/studio/insight-agents/insight-agents-hub').then(
                (m) => m.InsightAgentsHubPage
              ),
          },
          {
            path: 'insight-agents/:slug',
            loadComponent: () =>
              import('./pages/studio/insight-agents/insight-agent-shell/insight-agent-shell').then(
                (m) => m.InsightAgentShell
              ),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'schedule' },
              {
                path: 'schedule',
                loadComponent: () =>
                  import('./pages/studio/insight-agents/schedule/insight-agent-schedule').then(
                    (m) => m.InsightAgentSchedulePage
                  ),
              },
              {
                path: 'runs',
                loadComponent: () =>
                  import('./pages/studio/runs/studio-runs').then((m) => m.StudioRunsPage),
              },
              {
                path: 'versions',
                loadComponent: () =>
                  import('./pages/studio/insight-agents/versions/insight-agent-versions').then(
                    (m) => m.InsightAgentVersionsPage
                  ),
              },
            ],
          },
          {
            matcher: studioReportsMatcher,
            loadComponent: () =>
              import('./pages/pinned-reports/pinned-reports').then((m) => m.PinnedReportsPage),
          },
          {
            path: 'schedule',
            loadComponent: () =>
              import('./pages/studio/schedule/studio-schedule').then((m) => m.StudioSchedulePage),
          },
          {
            path: 'runs',
            loadComponent: () =>
              import('./pages/studio/runs/studio-runs').then((m) => m.StudioRunsPage),
          },
        ],
      },
      {
        // Back-compat for the pre-studio pinned-reports bookmark and nav link.
        path: 'pinned-reports',
        redirectTo: 'studio/reports',
        pathMatch: 'full',
      },
      {
        // GF-5426: redirect old bookmark to the new Admin shell location.
        path: 'cmi/config',
        redirectTo: '/admin/configuration',
      },
      {
        // GF-5425: admin shell — named parent component so all /admin/* routes
        // share a common ancestor. Children render into the shell's router-outlet.
        path: 'admin',
        loadComponent: () => import('./pages/admin/admin-shell').then((m) => m.AdminShell),
        children: [
          {
            // Landing page linking to the tools below, filtered to the caller's permissions.
            path: '',
            pathMatch: 'full',
            canActivate: [adminOverviewGuard],
            loadComponent: () =>
              import('./pages/admin/admin-overview/admin-overview').then((m) => m.AdminOverview),
          },
          {
            // GF-5426: Tenant Configuration (branding + CMI agent setup), migrated from /cmi/config.
            path: 'configuration',
            canActivate: [fullCmiGuard, configurationNavEnabledGuard],
            loadComponent: () =>
              import('./pages/cmi-config/cmi-config').then((m) => m.CMIConfigPage),
          },
          {
            // Componentless grouping for Data & Schema config tools (GF-5427).
            // No component here — children render in AdminShell's outlet directly.
            path: 'configs',
            children: [
              // Back-compat for the pre-GF-4213 `/admin/configs` bookmark.
              { path: '', pathMatch: 'full', redirectTo: 'ui' },
              {
                // GF-3897 UIConfig editor.
                path: 'ui',
                canActivate: [uiConfigAdminGuard],
                loadComponent: () =>
                  import('./pages/admin/configs/admin-configs').then((m) => m.AdminConfigs),
              },
              {
                // GF-4216 column-mapping editor.
                path: 'column-mapping',
                canActivate: [columnMappingAdminGuard],
                loadComponent: () =>
                  import('./pages/admin/column-mapping/admin-column-mapping').then(
                    (m) => m.AdminColumnMapping
                  ),
              },
            ],
          },
          {
            // GF-3991 tenant user access.
            path: 'memberships',
            canActivate: [tenantMembershipsAdminGuard],
            loadComponent: () =>
              import('./pages/admin/memberships/admin-memberships').then((m) => m.AdminMemberships),
          },
          {
            path: 'access-roles',
            canActivate: [accessRolesAdminGuard],
            loadComponent: () =>
              import('./pages/admin/access-roles/admin-access-roles').then(
                (m) => m.AdminAccessRoles
              ),
          },
          {
            // Listed before ':id' so "new" never resolves as a role id.
            path: 'access-roles/new',
            canActivate: [accessRolesAdminGuard],
            loadComponent: () =>
              import('./pages/admin/access-roles/access-role-editor').then(
                (m) => m.AccessRoleEditor
              ),
          },
          {
            path: 'access-roles/:id',
            canActivate: [accessRolesAdminGuard],
            loadComponent: () =>
              import('./pages/admin/access-roles/access-role-editor').then(
                (m) => m.AccessRoleEditor
              ),
          },
          {
            path: 'access-audit',
            canActivate: [accessRolesAdminGuard],
            loadComponent: () =>
              import('./pages/admin/access-audit/admin-access-audit').then(
                (m) => m.AdminAccessAudit
              ),
          },
          {
            // GF-4773 weekly-digest audience checklist.
            path: 'email-subscriptions',
            canActivate: [emailSubscriptionsAdminGuard],
            loadComponent: () =>
              import('./pages/admin/email-subscriptions/admin-email-subscriptions').then(
                (m) => m.AdminEmailSubscriptions
              ),
          },
          {
            // GF-4979 Review Bands run visibility.
            path: 'price-band-runs',
            canActivate: [priceBandRunAdminGuard],
            loadComponent: () =>
              import('./pages/admin/price-band-runs/admin-price-band-runs').then(
                (m) => m.AdminPriceBandRuns
              ),
          },
          {
            // GF-5630 data scopes: mode, scope rows, CSV import and columns.
            path: 'data-scopes',
            canActivate: [dataScopeAdminGuard],
            loadComponent: () =>
              import('./pages/admin/data-scopes/admin-data-scopes').then((m) => m.AdminDataScopes),
          },
          {
            // GF-4659 global user-permission grants (grant/revoke keys per user).
            path: 'permissions',
            canActivate: [userPermissionsAdminGuard],
            loadComponent: () =>
              import('./pages/admin/permissions/admin-permissions').then((m) => m.AdminPermissions),
          },
          {
            // Admin-only user-type administration (move a user between tiers).
            path: 'user-types',
            canActivate: [userTypesAdminGuard],
            loadComponent: () =>
              import('./pages/admin/user-types/admin-user-types').then((m) => m.AdminUserTypes),
          },
          {
            // Per-tenant Pricing Action Loop enablement (super-user permission gated).
            path: 'pricing-action',
            canActivate: [pricingActionAdminGuard],
            loadComponent: () =>
              import('./pages/admin/pricing-action/admin-pricing-action').then(
                (m) => m.AdminPricingAction
              ),
          },
          {
            // GF-5419 CSS embedded-Dive publishing (paste UUID, preview, publish).
            path: 'dive-publishing',
            canActivate: [diveEmbedAdminGuard],
            loadComponent: () =>
              import('./pages/admin/dive-publishing/admin-dive-publishing').then(
                (m) => m.AdminDivePublishing
              ),
          },
        ],
      },
      {
        // Where a capability guard sends a denial. Never guarded, so it can't loop.
        path: 'no-access',
        loadComponent: () => import('./pages/no-access/no-access').then((m) => m.NoAccessPage),
      },
      {
        // GF-4159 CSM insight review gate. Permission-gated against the DB
        // ``insights.review`` key. Lets super_users with the grant preview
        // the weekly staged insight batch, hide individual insights, and
        // publish the rest to all tenant users.
        path: 'admin/insight-review',
        canActivate: [fullCmiGuard, insightReviewGuard],
        loadComponent: () =>
          import('./pages/admin/insight-review/insight-review').then((m) => m.InsightReview),
      },
      // Unknown and disabled URLs alike: no guard, so it renders for every tenant and never loops.
      {
        path: '**',
        loadComponent: () => import('./layout/not-found-page').then((m) => m.NotFoundPage),
      },
    ],
  },
];

/**
 * Catalog of page-level "view all" aggregates derived from `routes`.
 *
 * Every route whose `data.cmiViewAllConfig.investigationType` is the
 * empty string is treated as a roll-up page (today: `/home/insights`).
 * Consumers (e.g. the chat page-context builder) read this constant
 * instead of hardcoding the list, so adding a new aggregate is a
 * matter of declaring its route here and nothing else.
 */
export const CMI_AGGREGATE_VIEWS = deriveCmiAggregateViews(routes);
